import {
  Link,
  Redirect,
  Route,
  Switch,
  useHistory,
  useLocation,
  useRouteMatch,
} from 'react-router-dom'
import { HelmetProvider, Toaster } from '../../../App'
import routes from '../../../router/routes'
import { Dialog, DialogContent } from '../../../shared_components/Digalog'
import { ComponentType, ReactNode, useCallback, useMemo } from 'react'
import { Box } from '../../../shared_components/UI/Box'
import styled, { css } from 'styled-components'
import {
  H2,
  P,
  Span,
  Strong,
} from '../../../shared_components/Library/Typeography'
import {
  BarryUIColorKey,
  customPadding,
  CustomPaddingProps,
  theme,
  useShouldShowForThreshold,
} from '../../../theme'
import useTwitterProfileImage from '../../../hooks/useTwitterProfileImage'
import { useSelector } from 'react-redux'
import { selectUser } from '../../../modules/user/selectors'
import Link01 from '../../../shared_components/Icons/Link01'
import Mail01 from '../../../shared_components/Icons/Mail01'
import { selectAccount } from '../../../modules/web3/selectors'
import { formatAccountAbbreviation } from '../../../shared_components/ConnectedWallet'
import useIsLoggedIn from '../../../hooks/useIsLoggedIn'
import Safe from '../../../shared_components/Icons/Safe'
import Communications from '../../Account/Communications'
import Socials from '../../Account/Socials'
import Profile from '../../Account/Profile'
import useNetworkId from '../../../hooks/useNetworkId'
import File from '../../../shared_components/Icons/File'
import TokensAndVaults from './TokensAndVaults'
import ClaimHistory from './ClaimHistory'
import MyZapMobileMenu from './MobileMenu'
import { useStakingContext } from '../../../contexts/StakingContext'
import Portfolio from './Portfolio'
import { usePageMaxWidth } from '../../../shared_components/Page'
import TokenDetails from './TokenDetails'
import { getUsernameFromUserObject } from '../../DropDetails/DropOverview'
import EditProfile from '../../../shared_components/Icons/Profile'

const myZapRoutes = routes.myzap

const Indicator = styled(Box) <{ isActive?: boolean }>`
  width: 18px;
  height: 18px;
  gap: 10px;
  border-radius: 40px;
  background: var(--surface-brand, #ff0);
  color: var(--text-invert, #080808);

  ${({ isActive }) =>
    isActive &&
    `
    background: var(--surface-primary, #080808);
    color: var(--text-brand-primary, #FF0);
  `}
`

export const TokensAndVaultsLabel = () => {
  const { pathname } = useLocation()
  const isActive = useMemo(
    () => myZapRoutes.tokensAndVaults === pathname,
    [pathname],
  )
  const { tokensClaimableFromVesting, tokensClaimableFromRewards } =
    useStakingContext()

  const claimsAvailableIndicatorNumber = useMemo(() => {
    let indicatorNumber = 0
    if (tokensClaimableFromVesting > 0n) indicatorNumber++
    if (tokensClaimableFromRewards > 0n) indicatorNumber++
    return indicatorNumber
  }, [tokensClaimableFromVesting, tokensClaimableFromRewards])

  return (
    <Box g={8}>
      Tokens & Vaults
      {claimsAvailableIndicatorNumber > 0 && (
        <Indicator jc ac isActive={isActive}>
          <P s="S" fw="semibold">
            {claimsAvailableIndicatorNumber}
          </P>
        </Indicator>
      )}
    </Box>
  )
}

const MyZap = () => {
  const { state, pathname } = useLocation()
  const { push } = useHistory()

  const isAtLeastMediumDisplaySize = useShouldShowForThreshold('compact')

  const user = useSelector(selectUser)
  const account = useSelector(selectAccount)
  const { isLoggedIn, isAuthenticating } = useIsLoggedIn()
  const { url: profileImgUrl } = useTwitterProfileImage(user)

  const isPortfolioPage = useMemo(() => myZapRoutes.tokensHeld === pathname, [pathname])

  if (!isLoggedIn && !isAuthenticating) return <Redirect to="/" />

  const userNameToDisplay = getUsernameFromUserObject(user, account) ?? formatAccountAbbreviation(
    account?.toLowerCase() ?? '',
  )

  return (
    <>
      <HelmetProvider title="ZAP | My ZAP" />
      <Box dc afs jfs fu mw="100vw" mh="100%">
        <Box padH={16} padT={!isAtLeastMediumDisplaySize && 24}>
          {isAtLeastMediumDisplaySize && (
            <>
              <SideBar jfs dc>
                <Box dc>
                  <ListGroup>
                    <ListHeader>
                      <Link to={myZapRoutes.tokensHeld} style={{ width: '100%' }}>
                        <Box
                          ac
                          g={8}
                          padV={8}
                          padH={8}
                          {
                          ...isPortfolioPage && ({
                            style: {
                              background: 'var(--surface-minimal-alpha-primary, rgba(255, 255, 255, 0.04))',
                              width: '100%',
                              borderRadius: 'var(--units-4, 4px)',
                            }
                          })
                          }
                        >
                          <Box g={8} fw>
                            <Box fu aw>
                              <img
                                src={profileImgUrl}
                                style={{
                                  height: '38px',
                                  width: '38px',
                                  borderRadius: '49.176px',
                                  border: '1px solid var(--stroke-secondary, #333)',
                                }}
                                alt="user avatar"
                              />
                            </Box>
                            <Box dc g={4}>
                              <Strong s="L" fw="semibold" c="text-primary">
                                {userNameToDisplay}
                              </Strong>
                              <Span s="S" fw="regular" c="text-secondary">
                                MyZap
                              </Span>
                            </Box>
                          </Box>
                        </Box>
                      </Link>
                    </ListHeader>
                    <Divider w="100%" h="1px" c="tertiary" padV="m" />
                    <P s="S" c="text-secondary" ta="right" padH={8}>Settings</P>
                    <ListItemSubgroup>
                      <ListItem
                        Icon={EditProfile}
                        label="Edit profile"
                        path={myZapRoutes.profile}
                      />
                      <ListItem
                        Icon={Link01}
                        label="Socials"
                        path={myZapRoutes.socials}
                      />
                      <ListItem
                        Icon={Mail01}
                        label="Communications"
                        path={myZapRoutes.communications}
                      />
                    </ListItemSubgroup>
                  </ListGroup>

                  <Divider w="100%" h="1px" c="tertiary" padV="m" />

                  <ListGroup>
                    <ListHeader label="Staking & Claiming" />
                    <ListItemSubgroup>
                      <ListItem
                        Icon={Safe}
                        label={<TokensAndVaultsLabel />}
                        path={myZapRoutes.tokensAndVaults}
                      />
                      <ListItem
                        Icon={File}
                        label="Claiming history"
                        path={myZapRoutes.claimingHistory}
                      />
                    </ListItemSubgroup>
                  </ListGroup>
                </Box>
              </SideBar>
              <Divider h="100vh" w="1px" c="tertiary" />
            </>
          )}
          <Box jc ac igmh={isAtLeastMediumDisplaySize}>
            <ContentBox jc igmv={isAtLeastMediumDisplaySize}>
              <Switch>
                <Route path={myZapRoutes.profile} component={Profile} />
                <Route exact path={myZapRoutes.tokensHeld} component={Portfolio} />
                <Route path={myZapRoutes.tokenDetails} component={TokenDetails} />
                <Route path={myZapRoutes.socials} component={Socials} />
                <Route
                  path={myZapRoutes.communications}
                  component={Communications}
                />
                <Route
                  path={myZapRoutes.tokensAndVaults}
                  component={TokensAndVaults}
                />

                <Route
                  path={myZapRoutes.claimingHistory}
                  component={ClaimHistory}
                />
                <Route path={myZapRoutes.menu} component={MyZapMobileMenu} />
              </Switch>
            </ContentBox>
          </Box>
        </Box>
      </Box>
    </>
  )
}

export default MyZap

const SideBar = styled(Box)`
  min-width: 260px;
  max-width: 260px;
  padding: var(--Index-Grids-Margins, 48px) var(--Spacing-Padding-L, 20px) 0px 0px;
  align-items: flex-start;
  gap: var(--Spacing-Padding-M, 16px);
  flex: 1 0 0;
`

const ContentBox = styled(Box)`
  max-width: 628px;
  align-self: center;

  @media screen and (max-width: ${({ theme }) => theme.layout.medium.indexGrids.maxWidth}px) {
    max-width: 484px;
  }
`

export const ItemBox = styled(Box) <{ isPadded?: boolean }>`
  background: transparent;
  width: 100%;
  align-self: stretch;
  border-radius: 8px;
  ${({ isPadded = true }) =>
    isPadded &&
    `padding: var(--units-12, 12px) var(--units-16, 16px) var(--units-12, 12px)
  var(--units-12, 12px);`}
`

function ModalOverlay({
  children,
  isOpen,
  dismiss,
}: {
  children: ReactNode
  isOpen: boolean
  dismiss: () => void
}) {
  return (
    // https://floating-ui.com/docs/dialog#reusable-dialog-component
    // Check the above to understand if you need to extend it
    <Dialog dismissOnClickOutside={false} onOpenChange={dismiss} open={isOpen}>
      <DialogContent
        style={{
          background: 'var(--surface-primary, #080808)',
          height: '100%',
          width: '100%',
          display: 'flex',
        }}
      >
        {children}
        <Toaster />
      </DialogContent>
    </Dialog>
  )
}

export function Divider({
  h,
  w,
  c,
  padV,
  padH,
  ...props
}: CustomPaddingProps & {
  h?: string
  w?: string
  c?: keyof typeof theme.semantics.zap.stroke
}) {
  return (
    <PaddingDivider h={h} w={w} padV={padV} padH={padH}>
      <DividerStyled h={h} w={w} c={c} />
    </PaddingDivider>
  )
}

const PaddingDivider = styled.div<
  {
    h?: string
    w?: string
    c?: keyof typeof theme.semantics.zap.stroke
  } & CustomPaddingProps
>`
  height: ${({ h }) => h};
  width: ${({ w }) => w};
  ${customPadding}
`

const DividerStyled = styled.div<{
  h?: string
  w?: string
  c?: keyof typeof theme.semantics.zap.stroke
}>`
  height: ${({ h }) => h};
  width: ${({ w }) => w};
  background: ${({ c }) => theme.semantics.zap.stroke[c]};
`

export function ListGroup({ children }: { children: ReactNode }) {
  return (
    <Box dc g={8}>
      {children}
    </Box>
  )
}

export function ListItemSubgroup({ children }: { children: ReactNode }) {
  return <Box dc>{children}</Box>
}

export function ListHeader({
  label,
  children,
}: {
  label?: string
  children?: ReactNode
}) {
  return (
    <Box dc padH={8}>
      {label && (
        <Strong s="S" fw="regular" c="text-secondary">
          {label}
        </Strong>
      )}
      {children}
    </Box>
  )
}

export function ListItem({
  Icon,
  label,
  path,
  disabled,
  textColor = 'text-secondary',
  children,
  showIndicator = false,
}: {
  Icon?: ComponentType<any>
  label?: ReactNode
  path: string
  disabled?: boolean
  textColor?: BarryUIColorKey
  children?: ReactNode
  showIndicator?: boolean
}) {
  const { pathname } = useLocation()
  const isActive = useMemo(() => path === pathname, [path, pathname])

  const isAtlestMediumDisplaySize = useShouldShowForThreshold('compact')

  return (
    <Link
      to={disabled ? undefined : path}
      style={{
        width: '100%',
        cursor: disabled ? 'not-allowed' : undefined,
        pointerEvents: disabled ? 'none' : undefined,
      }}
    >
      <ListItemStyled
        g={8}
        ac
        padV={isAtlestMediumDisplaySize ? 8 : 16}
        padH={isAtlestMediumDisplaySize ? 8 : 16}
        isActive={!disabled && isActive}
        disabled={disabled}
      >
        {!Icon && !label && children}
        {label && (
          <Box>
            <Box ac g={8}>
              {Icon && (
                <Box fu aw>
                  <Icon
                    color={'var(--stroke-primary, #FFFFFF)'}
                    strokeWidth={isActive ? 1.4 : 0.670909}
                  />
                </Box>
              )}
              <Box dc>
                <Span
                  s="M"
                  fw={isActive ? 'semibold' : 'regular'}
                  c={isActive ? 'text-primary' : textColor}
                >
                  {label}
                </Span>
              </Box>
            </Box>
            {showIndicator && <ChevRight />}
          </Box>
        )}
      </ListItemStyled>
    </Link>
  )
}

const ChevRight = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M7.5 15L12.5 10L7.5 5" stroke="white" stroke-width="0.670909" stroke-linecap="round" stroke-linejoin="round" />
  </svg>
)

const ListItemStyled = styled(Box) <{ isActive?: boolean; disabled?: boolean }>`
  border-radius: var(--units-4, 4px);
  background: transparent;

  ${({ isActive }) =>
    isActive &&
    css`
      background: ${({ theme }) => theme.semantics.zap.surface.minimalPrimary};
    `}

  ${({ disabled }) =>
    disabled &&
    css`
      opacity: 0.5;
    `}

  ${({ isActive }) =>
    !isActive &&
    `
    :hover {
      cursor: pointer;
      color: var(--text-primary, #FFF);
      background: var(--states-minimal-alpha-4, rgba(255, 255, 255, 0.04));
    }
  `}
`
